import {
    AddChannelToClientRequest,
    DeleteMessageRequest,
    Dialog,
    DialogAutoHoldingTimerResponse,
    DialogBadge,
    DialogFilesPageRequest,
    DialogFilesPageResponse,
    DialogUpdateFileSectionsRequest,
    DialogUpdateFileTitleRequest,
    DialogsSurveys,
    DialogsSurveysRequest,
    DialogStopAutoHoldingTimerRequest,
    EditMessageRequest,
    EditMessageResponse,
    GetDialogAttachment,
    GetDialogLastReroute,
    GetDialogOldResponse,
    GetDialogTransferAvailableQueuesRequest,
    GetMessagesHistoryRequest,
    HistoryDialogBadge,
    HistoryDialogBadgesRequest,
    IGetDialogTopicsRequest,
    IGetDialogTopicsResponse,
    IGetDialogTransferAvailableQueues,
    IGetDialogTransferAvailableQueuesByOperatorRequest,
    IGetOperatorChannelsResponse,
    IGetOperatorClientBadgesResponse,
    IGetOperatorClientRequest,
    IGetOperatorClientResponse,
    IGetOperatorsListRequest,
    IGetOperatorsListResponse,
    IGetUserChannelsRequest,
    IGetUserChannelsResponse,
    IOperatorDialogTransferToOperatorRequest,
    IOperatorDialogTransferToQueueRequest,
    ISearchClientsByCriterionResponse,
    ISearchOperatorsByCriterionRequest,
    ISearchOperatorsByCriterionResponse,
    ISetDialogChannelRequest,
    IUpdateDialogTopicsRequest,
    MessageActionsStatusResponse,
    OperatorFeedbackRequest,
    SearchUserRequest,
    DialogFilesPaginatedRequest
} from "../../models/Dialogs/dialog"
import { UpdateSlotContextChatUsersRequest, UpdateSlotContextRequest } from "../../models/Dialogs/surveyForm"
import {
    GetUpdatedMessagesRequest,
    GetUpdatedMessagesResponse,
    InternalMessageRequestDTO,
    ISendFinishRequest,
    MergedMessageRequest,
    MessageStatus,
    PinnedMessages,
    SendEventRequest,
    SendIntentRequest,
    SendInternalMessageResponse,
    SendMessageResponse,
    SendMessageStatuses,
    StartOutgoingDialogMessageRequest
} from "../../models/Dialogs/message"
import { cacher } from "../../utility/common/rtkQueryCacheUtils"
import { queryApi } from "../api"
import {
    activeDialogsCountTag,
    customSectionsTag,
    dialogBadgesTag,
    dialogMessagesHistoryTag,
    dialogMessagesTag,
    dialogsSurveys,
    dialogsTag,
    dialogTopicsTag,
    historyBadgesTag,
    operatorChannelsTag,
    operatorClientBadgesTag,
    operatorClientsSectionTitleTag,
    operatorClientTag,
    operatorsListTag,
    pinnedMessagesTag,
    userChannelsTag
} from "../tags"
import { CustomMenusValue, CustomSectionsValue } from "../../models/projectSettings"
import store from "../../store/store"
import { buildQueryString } from "../../helpers/url"
import { isSlotSysFieldToUpdate, SYS_SLOTS_TO_UPDATE_MAPPING } from "../../utility/dialogs/slots"
import { MaybeDrafted, PatchCollection } from "@reduxjs/toolkit/dist/query/core/buildThunks"
import { getAIAssistHint, getAISuggestsByClientId, getAISuggestsByDialogId } from "../../store/dialogs/thunks"
import { apiInstance } from "../instances/apiInstance"
import { LegacyDialog } from "../../models/Dialogs/legacyDialog"
import { actions } from "../../store/dialogs/slice"
import { v4 } from "uuid"
import {
    DELETE_MESSAGE_FAILED,
    EDIT_MESSAGE_FAILED,
    SEND_MESSAGE_FAILED,
    SEND_OPERATOR_FEEDBACK_FAILED
} from "../../store/dialogs/constants"
import { selectLastMessageInDialog, selectMessagesPaginationState } from "../../store/dialogs/selectors"
import {
    addDeleteEventToMessages,
    addEditEventToMessages,
    updateDialogMessageQueryId,
    updateDialogMessagesQuery
} from "../utility/dialogs.helpers"
import { AdditionalResendConfig } from "../../models/queue"
import { SimpleOperatorEmailChannel } from "../../models/channel"
import { FAKE_EMAIL_CHANNEL_ID_PREFIX } from "../../utility/channels/channel"
import {
    ClientSubscriptionRequest,
    ClientSubscriptionResponse,
    LinkClientRequest,
    LinkedToOperatorClient
} from "../../models/Dialogs/linksToOperator"
import { channelTypeStringConverter } from "../../utility/channels/channelTypeStringConverter"
import { HAS_ACTIVE_DIALOG_WITH_BOT, HAS_ACTIVE_DIALOG_WITH_OPERATOR, REQUEST_STATUS } from "../constants/dialogs.const"
import { handleQueryApiError, handleQueryApiFakeError } from "../utility/error"
import { v4 as uuidv4 } from "uuid"
import { saveOperatorFeedbackStatus } from "../../components/Dialogs/DialogOperatorFeedbackForm/utility/feedbackStorage"
import { selectDraftById } from "../../store/drafts/selectors"
import { selectCurrentUserLanguage } from "../../store/users/selectors"

const dialogsController = {
    get: (id: string): Promise<LegacyDialog> => apiInstance.get(`dialogs/${id}`).then(response => response.data)
}

export default dialogsController

export const updateDialogBadges = (callback: (oldState: MaybeDrafted<DialogBadge[]>) => void) => {
    return dialogsApi.util.updateQueryData(DialogsApiEndpoints.getDialogBadges, undefined, oldState => {
        callback(oldState)
    })
}

/*
  Во многих запросах здесь используется projectId
  Он бывает 2х видов:
  - тот, что у нас в редаксе выбран как активный на фронте на данный момент
  - тот, который был использован при создании диалога

  Пожалуйста, не забывайте эту специфику при разработке
  Чтобы понять, где какой projectId использовать, нужно почитать здесь -
  https://youtrack.craft-talk.ru/articles/CLOUD-A-11
*/

export const dialogsApi = queryApi.injectEndpoints({
    endpoints(build) {
        return {
            getOperatorClientsSectionTitle: build.query<string, string>({
                // queryFn: () => {
                //     return { data: mockedOperatorClientsSectionTitle }
                // },
                query: projectId => ({
                    url: `${projectId}/operator-clients/section-name`
                }),
                providesTags(result, error, arg) {
                    return cacher.cacheByIdArg(operatorClientsSectionTitleTag)(result, error, arg)
                }
            }),
            getOperatorClientsBadges: build.query<IGetOperatorClientBadgesResponse[], void>({
                // queryFn: () => {
                //     return { data: mockedOperatorClientBadges }
                // },
                query: () => ({
                    url: `/operator-clients`
                }),
                providesTags(result, error) {
                    const remapped = result?.map(x => ({
                        ...x,
                        Id: x.OmniUserId
                    }))

                    return cacher.providesList(operatorClientBadgesTag)(remapped, error)
                }
            }),
            searchClientsByCriterion: build.query<ISearchClientsByCriterionResponse[], string>({
                // queryFn: () => {
                //     return { data: mockedOperatorClientBadges }
                // }
                query: query => ({
                    url: `/user-profiles?query=${query}`
                })
            }),
            searchOmniuserByChannelId: build.query<string, SearchUserRequest>({
                // queryFn: () => {
                //     return { data: mockedOmniuserByChannelId }
                // }
                query: ({ projectId, channelId, channelUserId }) => ({
                    url: `/users/get_omni_user_id_by_channel_user_id?customer_id=${projectId}&channel_id=${channelId}&channel_user_id=${channelUserId}`,
                    method: "GET"
                }),
                transformResponse: (response: string | null): string => {
                    if (response === null) {
                        return ""
                    }
                    return response
                }
            }),
            getOperatorsList: build.query<IGetOperatorsListResponse[], IGetOperatorsListRequest>({
                // queryFn: () => {
                //     return { data: mockedOperatorsList }
                // },
                query: ({ projectId, operators: OperatorIds }) => ({
                    url: `operators/${projectId}/info`,
                    method: "POST",
                    body: {
                        OperatorIds
                    }
                }),
                providesTags(result, error, arg) {
                    const argsStr = JSON.stringify(arg)
                    return cacher.cacheByIdArg(operatorsListTag)(result, error, argsStr)
                }
            }),
            getCustomSections: build.query<CustomSectionsValue, string>({
                query: (projectId: string) => ({
                    url: `/custom-sections?projectId=${projectId}`
                }),
                providesTags(result, error) {
                    const sections = result?.Values
                    return cacher.providesList(customSectionsTag)(sections, error)
                }
            }),
            getPinnedMessages: build.query<PinnedMessages, string>({
                // queryFn: () => {
                //     return { data: [mockedMessages[2]] }
                // },
                query: (omniUserId: string) => ({
                    url: `/dialog/pinned_messages`,
                    method: "POST",
                    body: {
                        omniUserId
                    }
                }),
                providesTags(result, error, arg) {
                    return cacher.cacheByIdArg(pinnedMessagesTag)(result, error, arg)
                }
            }),
            getCustomMenus: build.query<CustomMenusValue, string>({
                query: (projectId: string) => ({
                    url: `/custom-menus?projectId=${projectId}`
                })
            }),
            getDialogBadges: build.query<DialogBadge[], void>({
                // queryFn: () => {
                //     return { data: mockedDialogBadges }
                // },
                query: () => ({
                    url: "/dialogs"
                }),
                providesTags(result, error) {
                    return cacher.providesList(dialogBadgesTag)(result, error)
                }
            }),
            getActiveDialogsCount: build.query<number, void>({
                // queryFn: () => {
                //     return { data: mockedActiveDialogsCount }
                // },
                query: () => ({
                    url: "/dialog/active-dialogs-total"
                }),
                providesTags(result, error) {
                    return cacher.cacheByIdArg(activeDialogsCountTag)(result, error, "messagesCount")
                }
            }),
            getDialogById: build.query<Dialog, string>({
                query: dialogId => ({
                    url: `/dialog/${dialogId}`
                })
            }),
            getMessagesById: build.query<GetUpdatedMessagesResponse, GetMessagesHistoryRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `dialog/messages?projectId=${ProjectId}`,
                    method: "POST",
                    body
                }),
                providesTags(result, error) {
                    const messages = result?.Messages.map(m => m.Fields)
                    return cacher.providesList(dialogMessagesHistoryTag)(messages, error)
                }
            }),
            getOperatorChannels: build.query<IGetOperatorChannelsResponse[], IGetOperatorClientRequest>({
                // queryFn: () => {
                //     return { data: mockedOperatorClient }
                // },
                query: ({ projectId, omniUserId }) => ({
                    url: buildQueryString({
                        baseUrl: "users/get_user_channels_info",
                        params: {
                            omni_user_id: omniUserId,
                            customer_id: projectId
                        }
                    })
                }),
                providesTags(result, error, arg) {
                    const argsStr = JSON.stringify(arg)
                    return cacher.cacheByIdArg(operatorChannelsTag)(result, error, argsStr)
                }
            }),
            getUserChannels: build.query<IGetUserChannelsResponse[], IGetUserChannelsRequest>({
                // queryFn: () => {
                //     return { data: mockedUserChannels }
                // },
                query: ({ projectId, omniUserId }) => ({
                    url: buildQueryString({
                        baseUrl: "users/get_user_channels",
                        params: {
                            omni_user_id: omniUserId,
                            customer_id: projectId
                        }
                    })
                }),
                providesTags(result, error, arg) {
                    const argsStr = JSON.stringify(arg)
                    return cacher.cacheByIdArg(userChannelsTag)(result, error, argsStr)
                },
                transformResponse: (response: IGetUserChannelsResponse[]): IGetUserChannelsResponse[] =>
                    response.filter(
                        (channel: IGetUserChannelsResponse) =>
                            !channel.channel_user_id.startsWith(FAKE_EMAIL_CHANNEL_ID_PREFIX)
                    )
            }),
            getOperatorClient: build.query<IGetOperatorClientResponse, IGetOperatorClientRequest>({
                // queryFn: () => {
                //     return { data: mockedOperatorClient }
                // },
                query: ({ projectId, omniUserId }) => ({
                    url: `${projectId}/chat-users/${omniUserId}`
                }),
                providesTags(result, error, arg) {
                    const argsStr = JSON.stringify(arg)
                    return cacher.cacheByIdArg(operatorClientTag)(result, error, argsStr)
                },
                async onQueryStarted({ omniUserId }, { dispatch, queryFulfilled }) {
                    const storeState = store.getState()
                    const storedMessage = selectDraftById(storeState, omniUserId)

                    if (storedMessage) {
                        dispatch(actions.getAISuggestReset())
                    }

                    const data = await queryFulfilled

                    const operatorLanguage = storeState.users.currentUser.data?.Locale
                    dispatch(
                        getAIAssistHint(
                            omniUserId,
                            data.data.Project.Id,
                            true,
                            undefined,
                            operatorLanguage
                        )
                    )
                    dispatch(getAISuggestsByClientId(omniUserId))
                }
            }),
            getDialog: build.query<Dialog, string>({
                // queryFn: () => {
                //     return { data: mockedDialog }
                // },
                query: dialogId => ({
                    url: `dialog/${dialogId}`
                }),
                providesTags(result, error, arg) {
                    return cacher.cacheByIdArg(dialogsTag)(result, error, arg)
                },
                async onQueryStarted(dialogId, { dispatch, queryFulfilled }) {
                    dispatch(actions.getAIAssistHintReset())
                    dispatch(actions.clearDialogEmailData())

                    const { data: dialog } = await queryFulfilled
                    const dialogProjectId = dialog?.Project.Id

                    dispatch(
                        getAIAssistHint(
                            dialog?.Client?.OmniUserId,
                            dialogProjectId,
                            false,
                            dialogId,
                            selectCurrentUserLanguage(store.getState())
                        )
                    )
                    dispatch(getAISuggestsByDialogId(dialogId))
                }
            }),
            getDialogsSurveys: build.query<DialogsSurveys, DialogsSurveysRequest>({
                query: ({ projectId, queueId }) => ({
                    url: buildQueryString({
                        baseUrl: `${projectId}/survey/dialog-surveys`,
                        params: {
                            queueId: queueId
                        }
                    })
                }),
                providesTags(result, error, arg) {
                    return cacher.cacheByIdArg(dialogsSurveys)(result, error, `${arg.projectId}-${arg.queueId}`)
                }
            }),
            getDialogMessages: build.query<GetUpdatedMessagesResponse, GetUpdatedMessagesRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `messages?projectId=${ProjectId}`,
                    method: "POST",
                    body
                }),
                providesTags(result, error) {
                    const messages = result?.Messages.map(m => m.Fields)
                    return cacher.providesList(dialogMessagesTag)(messages, error)
                }
            }),
            setDialogChannel: build.mutation<void, ISetDialogChannelRequest>({
                query: ({ dialogId, ...body }) => ({
                    url: `dialog/set_dialog_channel?dialogId=${dialogId}`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ channelId, channelType, dialogId }, { dispatch, queryFulfilled }) {
                    await queryFulfilled

                    dispatch(
                        updateDialogBadges(oldState => {
                            const idxIntoList = oldState.findIndex(x => x.Id === dialogId)

                            if (idxIntoList > -1) {
                                oldState[idxIntoList].Channel = {
                                    Id: channelId,
                                    Type: channelType
                                }
                            }
                        })
                    )
                }
            }),
            startOutgoingDialog: build.mutation<void, StartOutgoingDialogMessageRequest>({
                query: ({ ...body }) => ({
                    url: `dialog/start_outgoing_dialog`,
                    method: "POST",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled
                    } catch (e) {
                        handleQueryApiError(dispatch, SEND_MESSAGE_FAILED, e)
                    }
                }
            }),
            sendOperatorIsTyping: build.mutation<boolean, SendEventRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `message/send-operator-is-typing?projectId=${ProjectId}`,
                    method: "POST",
                    body
                })
            }),
            sendOperatorStoppedTyping: build.mutation<boolean, SendEventRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `message/send-operator-stopped-typing?projectId=${ProjectId}`,
                    method: "POST",
                    body
                })
            }),
            sendOperatorOpenedDialogView: build.mutation<boolean, SendEventRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `message/send-operator-opened-dialog-view?projectId=${ProjectId}`,
                    method: "POST",
                    body
                })
            }),
            sendOperatorClosedDialogView: build.mutation<boolean, SendEventRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `message/send-operator-closed-dialog-view?projectId=${ProjectId}`,
                    method: "POST",
                    body
                })
            }),
            sendFinish: build.mutation<boolean, ISendFinishRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `/message/send-finish?projectId=${ProjectId}`,
                    method: "POST",
                    body
                }),
                onQueryStarted(_, { dispatch }) {
                    dispatch(actions.closeFastRepliesSidebar())
                    dispatch(actions.closeKnowledgeBaseSidebar())
                    dispatch(
                        actions.setIsUserTyping({
                            isTyping: false,
                            message: ""
                        })
                    )
                },
                invalidatesTags: ["HistoryBadges"]
            }),
            sendIntent: build.mutation<boolean, SendIntentRequest>({
                query: ({ ProjectId, ...body }) => ({
                    url: `message/send-intent?projectId=${ProjectId}`,
                    method: "POST",
                    body
                })
            }),
            sendCommand: build.mutation<SendMessageResponse, MergedMessageRequest>({
                query: ({ message, projectId }) => ({
                    url: `/message/send-message?projectId=${projectId}`,
                    method: "POST",
                    body: message
                }),
                invalidatesTags: (res, err, arg) => {
                    const id = arg.message.OmniUserId
                    return [{ type: pinnedMessagesTag, id: id }]
                }
            }),
            sendInternalMessage: build.mutation<SendInternalMessageResponse, InternalMessageRequestDTO>({
                query: ({ message, projectId }) => ({
                    url: `message/send-internal-message?projectId=${projectId}`,
                    method: "POST",
                    body: message
                }),
                async onQueryStarted({ message, body }, { dispatch, queryFulfilled }) {
                    const currentOperator = store.getState().users.currentUser?.data?.Login
                    const selectedDialogId = store.getState().dialogs.selectedDialogId

                    const draftedMessageId = v4()
                    let getDialogMessagesPatchResult: PatchCollection | undefined = undefined

                    let dialogData: Dialog | null = null

                    if (selectedDialogId) {
                        const getDialogQuery = dialogsApi.endpoints.getDialog.select(selectedDialogId)(store.getState())
                        if (getDialogQuery.data) {
                            dialogData = getDialogQuery.data
                        }
                    }

                    if (dialogData) {
                        getDialogMessagesPatchResult = updateDialogMessagesQuery(
                            dispatch,
                            { ...body, ProjectId: dialogData.Project.Id },
                            draftedMessageId,
                            draftedMessageId,
                            message,
                            currentOperator,
                            undefined,
                            undefined,
                            true
                        )
                    }

                    try {
                        await queryFulfilled
                    } catch (e) {
                        handleQueryApiError(dispatch, SEND_MESSAGE_FAILED, e, [getDialogMessagesPatchResult])
                    }
                }
            }),
            sendMessage: build.mutation<SendMessageResponse, MergedMessageRequest>({
                query: ({ message, projectId }) => ({
                    url: `/message/send-message?projectId=${projectId}`,
                    method: "POST",
                    body: message
                }),
                async onQueryStarted({ message, body, projectId, quotedMessage }, { dispatch, queryFulfilled }) {
                    const currentOperator = store.getState().users.currentUser?.data?.Login
                    const selectedDialogId = store.getState().dialogs.selectedDialogId

                    let dialogData: Dialog | null = null

                    if (selectedDialogId) {
                        const getDialogQuery = dialogsApi.endpoints.getDialog.select(selectedDialogId)(store.getState())
                        if (getDialogQuery.data) {
                            dialogData = getDialogQuery.data
                        }
                    }

                    const getDialogBadgesPatchResult = dispatch(
                        updateDialogBadges(oldState => {
                            const idxIntoList = oldState.findIndex(x => x.Client.OmniUserId === message.OmniUserId)

                            if (idxIntoList > -1) {
                                if (message.Text) {
                                    oldState[idxIntoList].Preview = message.Text
                                } else if (message.Attachments?.length) {
                                    oldState[idxIntoList].Preview = "dialogs:dialogs-list.description.attachment"
                                } else if (message.Sticker?.sticker_id) {
                                    oldState[idxIntoList].Preview = "dialogs:dialogs-list.description.sticker"
                                }
                            }
                        })
                    )

                    const draftedMessageId = v4()
                    let getDialogMessagesPatchResult: PatchCollection | undefined = undefined

                    if (dialogData) {
                        getDialogMessagesPatchResult = updateDialogMessagesQuery(
                            dispatch,
                            { ...body, ProjectId: dialogData.Project.Id },
                            draftedMessageId,
                            draftedMessageId,
                            message,
                            currentOperator,
                            quotedMessage
                        )
                    }
                    try {
                        const { data: sendMessageResponse } = await queryFulfilled
                        switch (sendMessageResponse.Status) {
                            case SendMessageStatuses.HasActiveDialogWithBot:
                            case SendMessageStatuses.HasActiveDialogWithOperator:
                                if (currentOperator) {
                                    getDialogMessagesPatchResult = updateDialogMessagesQuery(
                                        dispatch,
                                        { ...body, ProjectId: projectId },
                                        draftedMessageId,
                                        sendMessageResponse.MessageId,
                                        message,
                                        currentOperator,
                                        quotedMessage,
                                        MessageStatus.Failed
                                    )
                                }

                                const errorReason =
                                    sendMessageResponse.Status === SendMessageStatuses.HasActiveDialogWithBot
                                        ? HAS_ACTIVE_DIALOG_WITH_BOT
                                        : HAS_ACTIVE_DIALOG_WITH_OPERATOR

                                handleQueryApiFakeError(dispatch, errorReason)
                                break
                            case SendMessageStatuses.NewDialogWillBeStarted:
                            case SendMessageStatuses.SentToExistingDialog:
                                if (currentOperator && dialogData) {
                                    getDialogMessagesPatchResult = updateDialogMessagesQuery(
                                        dispatch,
                                        { ...body, ProjectId: dialogData.Project.Id },
                                        draftedMessageId,
                                        sendMessageResponse.MessageId,
                                        message,
                                        currentOperator,
                                        quotedMessage,
                                        MessageStatus.Sent
                                    )
                                }
                                break
                            default:
                                throw new Error(REQUEST_STATUS + sendMessageResponse.Status)
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, SEND_MESSAGE_FAILED, e, [
                            getDialogMessagesPatchResult,
                            getDialogBadgesPatchResult
                        ])
                    }
                }
            }),
            postUpdateSlotContext: build.mutation<boolean, UpdateSlotContextRequest>({
                query: ({ DialogId, UpdatedSlots }) => ({
                    url: `dialog/update_slot_context?dialogId=${DialogId}`,
                    method: "POST",
                    body: { UpdatedSlots }
                }),
                async onQueryStarted({ DialogId, UpdatedSlots }, { dispatch, queryFulfilled }) {
                    if (!DialogId) {
                        return
                    }

                    const getDialogPatchResult = dispatch(
                        dialogsApi.util.updateQueryData("getDialog", DialogId, oldState => {
                            UpdatedSlots.forEach(slot => {
                                if (isSlotSysFieldToUpdate(slot)) {
                                    oldState.Client[SYS_SLOTS_TO_UPDATE_MAPPING[slot.Id]] = slot.Value
                                }

                                const foundedSlotIndex = oldState.SlotContext.FilledSlots.findIndex(
                                    oldSlot => slot.Id === oldSlot.Id
                                )

                                if (foundedSlotIndex !== -1) {
                                    oldState.SlotContext.FilledSlots[foundedSlotIndex].Value = slot.Value
                                } else {
                                    oldState.SlotContext.FilledSlots.push(slot)
                                }
                            })
                        })
                    )

                    queryFulfilled.catch(getDialogPatchResult.undo)

                    const getDialogBadgesPatchResult = dispatch(
                        updateDialogBadges(oldState => {
                            const newState = oldState.map(badge => {
                                let newBadge = badge
                                if (badge.Id === DialogId) {
                                    UpdatedSlots.forEach(slot => {
                                        if (isSlotSysFieldToUpdate(slot)) {
                                            newBadge = {
                                                ...newBadge,
                                                Client: {
                                                    ...newBadge.Client,
                                                    [SYS_SLOTS_TO_UPDATE_MAPPING[slot.Id]]: slot.Value
                                                }
                                            }
                                        }
                                    })
                                }
                                return newBadge
                            })
                            Object.assign(oldState, newState)
                        })
                    )

                    queryFulfilled.catch(getDialogBadgesPatchResult.undo)
                },
                invalidatesTags: []
            }),
            postUpdateSlotContextChatUsers: build.mutation<boolean, UpdateSlotContextChatUsersRequest>({
                query: ({ ProjectId, OmniUserId, UpdatedSlots }) => ({
                    url: `${ProjectId}/chat-users/${OmniUserId}/slot_context`,
                    method: "POST",
                    body: { UpdatedSlots }
                }),
                async onQueryStarted({ OmniUserId, UpdatedSlots, ProjectId }, { dispatch, queryFulfilled }) {
                    if (!OmniUserId) {
                        return
                    }

                    const getOperatorClientPatchResult = dispatch(
                        dialogsApi.util.updateQueryData(
                            "getOperatorClient",
                            { projectId: ProjectId, omniUserId: OmniUserId },
                            oldState => {
                                UpdatedSlots.forEach(slot => {
                                    if (isSlotSysFieldToUpdate(slot)) {
                                        oldState[SYS_SLOTS_TO_UPDATE_MAPPING[slot.Id]] = slot.Value
                                        const findedSlotIndex = oldState.SlotContext.FilledSlots.findIndex(
                                            oldSlot => slot.Id === oldSlot.Id
                                        )

                                        if (findedSlotIndex !== -1) {
                                            oldState.SlotContext.FilledSlots[findedSlotIndex].Value = slot.Value
                                        }
                                    }
                                })
                            }
                        )
                    )

                    queryFulfilled.catch(getOperatorClientPatchResult.undo)

                    const getOperatorClientBadgesPatchResult = dispatch(
                        dialogsApi.util.updateQueryData("getOperatorClientsBadges", undefined, oldState => {
                            const newState = oldState.map(badge => {
                                let newBadge = badge
                                if (badge.OmniUserId === OmniUserId) {
                                    UpdatedSlots.forEach(slot => {
                                        if (isSlotSysFieldToUpdate(slot)) {
                                            newBadge = {
                                                ...newBadge,
                                                [SYS_SLOTS_TO_UPDATE_MAPPING[slot.Id]]: slot.Value
                                            }
                                        }
                                    })
                                }
                                return newBadge
                            })
                            Object.assign(oldState, newState)
                        })
                    )

                    queryFulfilled.catch(getOperatorClientBadgesPatchResult.undo)
                },
                invalidatesTags: []
            }),
            getDialogTopics: build.query<IGetDialogTopicsResponse[], IGetDialogTopicsRequest>({
                query: ({ ProjectId }) => ({
                    url: `${ProjectId}/dialog-topics`,
                    method: "GET"
                }),
                providesTags(result, error) {
                    return cacher.providesList(dialogTopicsTag)(result, error)
                },
                onQueryStarted(_, { dispatch, queryFulfilled }) {
                    queryFulfilled.then(({ data }) => {
                        dispatch(actions.setTopicsData(data))
                    })
                }
            }),
            putUpdateDialogTopics: build.mutation<void, IUpdateDialogTopicsRequest>({
                query: ({ DialogId, ...body }) => ({
                    url: `dialog/topics?dialogId=${DialogId}`,
                    method: "PUT",
                    body
                }),
                async onQueryStarted({ DialogId, ...body }, { dispatch, queryFulfilled }) {
                    const getDialogPatchResult = dispatch(
                        dialogsApi.util.updateQueryData("getDialog", DialogId, oldState => {
                            oldState.Topics = body.Topics
                        })
                    )

                    await queryFulfilled.catch(getDialogPatchResult.undo)
                },
                invalidatesTags: []
            }),
            searchOperatorByCriterion: build.query<
                ISearchOperatorsByCriterionResponse[],
                ISearchOperatorsByCriterionRequest
            >({
                /* queryFn: ({ query, queues, roles, statuses }) => {
                    console.log(">>>", query, queues, roles, statuses)

                    const randCount = random(10, 30)
                    const mockedRands = []

                    for (let i = 0; i < randCount; i++) {
                        const randIdx = Math.floor(Math.random() * 3)
                        mockedRands.push(mockedSearchOperatorsByCriterion[randIdx])
                    }

                    return { data: mockedRands }
                }, */
                query: ({ projectId, ...body }) => ({
                    url: `${projectId}/operators/search-for-reroute`,
                    method: "POST",
                    body: {
                        SearchQuery: body.query,
                        Roles: body.roles,
                        Queues: body.queues,
                        Size: 100,
                        IncludeCurrentUser: body.includeCurrentUser
                    }
                }),
                onQueryStarted(args, { dispatch, queryFulfilled }) {
                    queryFulfilled.then(({ data }) => {
                        dispatch(
                            actions.setSearchOperatorByCriterionRelatedData({
                                args,
                                response: data
                            })
                        )
                    })
                }
            }),
            operatorDialogTransferToQueue: build.mutation<void, IOperatorDialogTransferToQueueRequest>({
                query: ({ DialogId, ...body }) => ({
                    url: `dialog/reroute_to_queue?dialogId=${DialogId}`,
                    method: "POST",
                    body
                })
            }),
            operatorDialogTransferToOperator: build.mutation<void, IOperatorDialogTransferToOperatorRequest>({
                query: ({ DialogId, ByLink = false, ...body }) => ({
                    url: `dialog/reroute_to_operator?dialogId=${DialogId}&byLink=${ByLink}`,
                    method: "POST",
                    body
                })
            }),
            getDialogTransferAvailableQueues: build.query<
                IGetDialogTransferAvailableQueues,
                GetDialogTransferAvailableQueuesRequest
            >({
                query: ({ projectId, queueId, channelId }) => ({
                    url: buildQueryString({
                        baseUrl: `${projectId}/queues/queues-for-reroute`,
                        params: {
                            queueId,
                            channelId
                        }
                    })
                })
            }),
            getDialogTransferAvailableQueuesByOperator: build.query<
                IGetDialogTransferAvailableQueues,
                IGetDialogTransferAvailableQueuesByOperatorRequest
            >({
                query: ({ projectId, operatorId, channelId }) => ({
                    url: buildQueryString({
                        baseUrl: `${projectId}/operators/${operatorId}/queues-for-reroute`,
                        params: {
                            channelId
                        }
                    })
                })
            }),
            postEnableDialogHold: build.mutation<void, string>({
                query: dialogId => ({
                    url: `dialogs/hold/enable?dialogId=${dialogId}`,
                    method: "POST"
                }),
                async onQueryStarted(dialogId, { dispatch, queryFulfilled }) {
                    const getDialogBadgesPatchResult = dispatch(
                        updateDialogBadges(oldState => {
                            const dialogById = oldState.findIndex(d => d.Id === dialogId)

                            if (dialogById > -1) {
                                oldState[dialogById].IsHoldEnabled = true
                            }
                        })
                    )

                    await queryFulfilled.catch(getDialogBadgesPatchResult.undo)
                }
            }),
            putDisableDialogHold: build.mutation<void, string>({
                query: dialogId => ({
                    url: `dialogs/hold/disable?dialogId=${dialogId}`,
                    method: "PUT"
                }),
                async onQueryStarted(dialogId, { dispatch, queryFulfilled }) {
                    const getDialogBadgesPatchResult = dispatch(
                        updateDialogBadges(oldState => {
                            const dialogById = oldState.findIndex(d => d.Id === dialogId)

                            if (dialogById > -1) {
                                oldState[dialogById].IsHoldEnabled = false
                            }
                        })
                    )

                    await queryFulfilled.catch(getDialogBadgesPatchResult.undo)
                }
            }),
            postDeleteMessage: build.mutation<MessageActionsStatusResponse, DeleteMessageRequest>({
                query: ({ projectId, DeleteMessageBody }) => ({
                    url: `message/delete-message?projectId=${projectId}`,
                    method: "POST",
                    body: DeleteMessageBody
                }),
                async onQueryStarted({ DeleteMessageBody }, { dispatch, queryFulfilled }) {
                    let getDialogMessagesPatchResult
                    let getDialogBadgesPatchResult
                    try {
                        const lastMessage = selectLastMessageInDialog(store.getState())
                        const currentMessagePagination = selectMessagesPaginationState(store.getState())

                        if (currentMessagePagination) {
                            addDeleteEventToMessages(dispatch, currentMessagePagination, DeleteMessageBody)
                        }

                        const { data: status } = await queryFulfilled
                        if (status === MessageActionsStatusResponse.Success && currentMessagePagination) {
                            getDialogBadgesPatchResult = dispatch(
                                updateDialogBadges(oldState => {
                                    if (lastMessage?.Fields.Id === DeleteMessageBody.Id) {
                                        oldState[0].Preview = "dialogs:info.deleted-text-placeholder"
                                    }
                                })
                            )
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, DELETE_MESSAGE_FAILED, e, [
                            getDialogMessagesPatchResult,
                            getDialogBadgesPatchResult
                        ])
                    }
                }
            }),
            postEditMessage: build.mutation<EditMessageResponse, EditMessageRequest>({
                query: ({ projectId, EditMessageBody }) => ({
                    url: `message/edit-message?projectId=${projectId}`,
                    method: "POST",
                    body: EditMessageBody
                }),
                async onQueryStarted({ EditMessageBody }, { dispatch, queryFulfilled }) {
                    let getDialogMessagesPatchResult
                    let getDialogBadgesPatchResult

                    try {
                        const lastMessage = selectLastMessageInDialog(store.getState())
                        const currentMessagePagination = selectMessagesPaginationState(store.getState())
                        const currentOperator = store.getState().users.currentUser

                        if (currentMessagePagination) {
                            const operatorOmniUserId = currentOperator.data?.Login
                            const draftId = uuidv4()
                            if (operatorOmniUserId) {
                                addEditEventToMessages(
                                    dispatch,
                                    currentMessagePagination,
                                    operatorOmniUserId,
                                    EditMessageBody,
                                    draftId
                                )
                            }

                            const {
                                data: { Status, MessageId }
                            } = await queryFulfilled
                            if (Status === MessageActionsStatusResponse.Success && operatorOmniUserId) {
                                getDialogBadgesPatchResult = dispatch(
                                    updateDialogBadges(oldState => {
                                        if (lastMessage?.Fields.Id === EditMessageBody.Id) {
                                            oldState[0].Preview = EditMessageBody.Text
                                        }
                                    })
                                )
                                getDialogMessagesPatchResult = updateDialogMessageQueryId(
                                    dispatch,
                                    currentMessagePagination,
                                    draftId,
                                    MessageId,
                                    currentOperator.data?.Login
                                )
                            } else {
                                throw new Error("Request Status: " + Status)
                            }
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, EDIT_MESSAGE_FAILED, e, [
                            getDialogMessagesPatchResult,
                            getDialogBadgesPatchResult
                        ])
                    }
                }
            }),
            getOperatorEmailChannelsByDialog: build.mutation<SimpleOperatorEmailChannel[], string>({
                query: dialogId => ({
                    url: `email_channels_by_dialog?dialogId=${dialogId}`,
                    method: "GET"
                })
            }),
            getEmailForwardTemplate: build.query<AdditionalResendConfig, string>({
                query: dialogId => ({
                    url: `dialog/additional_resend_config_template?dialogId=${dialogId}`,
                    method: "GET"
                })
            }),
            // TODO: Костыль https://youtrack.craft-talk.ru/issue/CLOUD-5135
            // Бэкенд отдает неактульную информацию в getDialog касательно текущей очереди и оператора
            // Нужно будет исправить бэкенд
            getDialogOld: build.query<GetDialogOldResponse, string>({
                query: dialogId => ({
                    url: `dialogs/${dialogId}`,
                    method: "GET"
                })
            }),
            getDialogHistoryBadges: build.query<HistoryDialogBadge[], HistoryDialogBadgesRequest>({
                query: searchRequest => ({
                    url: `dialogs/get_history_dialog_badges`,
                    method: "POST",
                    body: searchRequest
                }),
                providesTags(result, error) {
                    const remapped = result?.map(x => ({
                        ...x,
                        Id: x.DialogId
                    }))
                    return cacher.providesList(historyBadgesTag)(remapped, error)
                }
            }),
            getLinkedToOperatorClients: build.query<LinkedToOperatorClient[], boolean>({
                query: onlyLinkedPersonal => ({
                    url: `/linked-clients?onlyLinkedPersonal=${onlyLinkedPersonal}`,
                    method: "GET"
                })
            }),
            searchLinkedClients: build.query<LinkedToOperatorClient[], { onlyLinkedPersonal: boolean; query: string }>({
                query: ({ onlyLinkedPersonal, query }) => ({
                    url: `/search-linked-clients?onlyLinkedPersonal=${onlyLinkedPersonal}&query=${query}`,
                    method: "GET"
                })
            }),
            postClientSubscriptions: build.query<ClientSubscriptionResponse[], ClientSubscriptionRequest>({
                query: data => ({
                    url: `/search-to-link`,
                    method: "POST",
                    body: data
                })
            }),
            linkClientToOperator: build.query<void, LinkClientRequest>({
                query: data => ({
                    url: `/link-client-to-operator`,
                    method: "POST",
                    body: data
                })
            }),
            unlinkClientToOperator: build.query<void, LinkClientRequest>({
                query: data => ({
                    url: `/unlink-client-from-operator`,
                    method: "DELETE",
                    body: data
                })
            }),
            linkClientToRole: build.query<void, LinkClientRequest>({
                query: data => ({
                    url: `/link-client-to-role`,
                    method: "POST",
                    body: data
                })
            }),
            unlinkClientToRole: build.query<void, LinkClientRequest>({
                query: data => ({
                    url: `/unlink-client-from-role`,
                    method: "DELETE",
                    body: data
                })
            }),
            getDialogFilesPage: build.query<DialogFilesPageResponse[], DialogFilesPaginatedRequest>({
                query: body => {
                    type PaginatedRequest = DialogFilesPaginatedRequest

                    const { page, ...apiBody } = body as PaginatedRequest

                    return {
                        url: `dialog/files/page`,
                        method: "POST",
                        body: apiBody
                    }
                },
                serializeQueryArgs: ({ queryArgs, endpointName }) => {
                    type QueryArgsWithPage = DialogFilesPageRequest & { page?: number; attachmentsType?: string }
                    const { From, Size, page, ...rest } = queryArgs as QueryArgsWithPage

                    const cacheKey = `${endpointName}(${JSON.stringify(rest)})`
                    return cacheKey
                },
                merge: (currentCache, newItems, { arg }) => {
                    if (arg.From === 0) {
                        return newItems
                    }

                    const existingUrls = new Set(currentCache.map(item => item.Url))
                    const uniqueNewItems = newItems.filter(item => !existingUrls.has(item.Url))

                    return [...currentCache, ...uniqueNewItems]
                },
                forceRefetch: ({ currentArg, previousArg }) => {
                    if (!previousArg) return true

                    type QueryArgsWithPage = DialogFilesPaginatedRequest
                    const {
                        From: prevFrom,
                        Size: prevSize,
                        page: prevPage,
                        ...prevRest
                    } = previousArg as QueryArgsWithPage
                    const {
                        From: currFrom,
                        Size: currSize,
                        page: currPage,
                        ...currRest
                    } = currentArg as QueryArgsWithPage

                    const prevRestStr = JSON.stringify(prevRest)
                    const currRestStr = JSON.stringify(currRest)
                    return prevRestStr !== currRestStr
                }
            }),
            updateFileTitle: build.mutation<void, DialogUpdateFileTitleRequest>({
                query: body => ({
                    url: `dialog/update_file_title`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ Url, Title }, { dispatch, queryFulfilled, getState }) {
                    const state = getState()

                    const queryCache = state.queryApi?.queries || {}

                    const patchResults: { undo: () => void }[] = []

                    Object.entries(queryCache).forEach(([queryCacheKey, queryData]) => {
                        if (queryCacheKey.startsWith("getDialogFilesPage")) {
                            const originalArgs = queryData?.originalArgs as DialogFilesPaginatedRequest

                            if (originalArgs && originalArgs.CustomerId) {
                                const patchResult = dispatch(
                                    dialogsApi.util.updateQueryData("getDialogFilesPage", originalArgs, draft => {
                                        const fileIndex = draft.findIndex(file => file.Url === Url)
                                        if (fileIndex !== -1) {
                                            draft[fileIndex].Title = Title
                                        }
                                    })
                                )
                                patchResults.push(patchResult)
                            }
                        }
                    })

                    queryFulfilled.catch(() => {
                        patchResults.forEach(patchResult => patchResult.undo())
                    })
                }
            }),
            updateFileSections: build.mutation<void, DialogUpdateFileSectionsRequest>({
                query: body => ({
                    url: `dialog/update_file_sections`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ Url, CustomerId, FileSections }, { dispatch, queryFulfilled, getState }) {
                    const state = getState()
                    const queryCache = state.queryApi?.queries || {}
                    const patchResults: { undo: () => void }[] = []

                    const updatedFileSections = new Set(FileSections)

                    let fileData: DialogFilesPageResponse | null = null
                    let currentFileSections: string[] = []

                    Object.entries(queryCache).forEach(([queryCacheKey, queryData]) => {
                        if (!fileData && queryCacheKey.startsWith("getDialogFilesPage")) {
                            const originalArgs = queryData?.originalArgs as DialogFilesPaginatedRequest
                            const data = queryData?.data as DialogFilesPageResponse[] | undefined

                            if (data && originalArgs && originalArgs.CustomerId === CustomerId) {
                                const foundFile = data.find(file => file.Url === Url)
                                if (foundFile) {
                                    currentFileSections = [...foundFile.File_sections]
                                    fileData = { ...foundFile, File_sections: FileSections }
                                }
                            }
                        }
                    })

                    const removedSections = currentFileSections.filter(section => !updatedFileSections.has(section))

                    Object.entries(queryCache).forEach(([queryCacheKey, queryData]) => {
                        if (queryCacheKey.startsWith("getDialogFilesPage")) {
                            const originalArgs = queryData?.originalArgs as DialogFilesPaginatedRequest

                            if (originalArgs && originalArgs.CustomerId === CustomerId) {
                                const patchResult = dispatch(
                                    dialogsApi.util.updateQueryData("getDialogFilesPage", originalArgs, draft => {
                                        const isSpecificSectionQuery =
                                            originalArgs.FileSections && originalArgs.FileSections.length > 0

                                        const sectionMatches =
                                            !isSpecificSectionQuery ||
                                            originalArgs.FileSections.some(section => updatedFileSections.has(section))

                                        const isSectionRemovedQuery =
                                            isSpecificSectionQuery &&
                                            originalArgs.FileSections.length === 1 &&
                                            removedSections.includes(originalArgs.FileSections[0])

                                        const fileIndex = draft.findIndex(file => file.Url === Url)

                                        if (fileIndex !== -1) {
                                            if (isSectionRemovedQuery) {
                                                draft.splice(fileIndex, 1)
                                            } else {
                                                draft[fileIndex].File_sections = FileSections
                                            }
                                        } else if (
                                            sectionMatches &&
                                            fileData &&
                                            isSpecificSectionQuery &&
                                            !isSectionRemovedQuery
                                        ) {
                                            draft.unshift(fileData)
                                        }
                                    })
                                )
                                patchResults.push(patchResult)
                            }
                        }
                    })

                    queryFulfilled.catch(() => {
                        patchResults.forEach(patchResult => patchResult.undo())
                    })
                }
            }),
            getDialogAttachments: build.query<GetDialogAttachment[], { dialogId: string; fromHistory: boolean }>({
                query: ({ dialogId, fromHistory }) => ({
                    url: `dialog/export_dialog_attachment_urls?dialogId=${dialogId}&fromHistory=${fromHistory}`,
                    method: "POST"
                })
            }),
            getDialogLastReroute: build.query<GetDialogLastReroute, string>({
                query: dialogId => ({
                    url: `/dialog/${dialogId}/last-reroute`,
                    method: "GET"
                })
            }),
            deleteRoutingMessage: build.mutation<GetDialogAttachment[], { dialogId: string }>({
                query: ({ dialogId }) => ({
                    url: `dialog/routing_message?dialogId=${dialogId}`,
                    method: "DELETE"
                }),
                async onQueryStarted({ dialogId }, { dispatch, queryFulfilled }) {
                    const getDialogPatchResult = dispatch(
                        dialogsApi.util.updateQueryData("getDialog", dialogId, draft => {
                            draft.RoutingMessage = undefined
                        })
                    )

                    await queryFulfilled.catch(getDialogPatchResult.undo)
                }
            }),
            getAutoHoldingTimer: build.query<DialogAutoHoldingTimerResponse, string>({
                query: dialogId => ({
                    url: `get_autoholding_timer?dialogId=${dialogId}`,
                    method: "GET"
                }),
                async onQueryStarted(dialogId, { dispatch, queryFulfilled }) {
                    const { data: AutoHoldingResponse } = await queryFulfilled

                    if (!AutoHoldingResponse.AutoHolding) {
                        dispatch(actions.removeAutoHoldingTimerFromDialog(dialogId))
                        return
                    }

                    const { DialogId, AutoHolding } = AutoHoldingResponse

                    dispatch(
                        actions.setAutoHoldingTimerForDialog({
                            DialogId: DialogId,
                            AutoHolding: AutoHolding
                        })
                    )
                }
            }),
            stopAutoHoldingTimer: build.mutation<void, { dialogId: string; body: DialogStopAutoHoldingTimerRequest }>({
                query: ({ dialogId, body }) => ({
                    url: `stop_autoholding_timer?dialogId=${dialogId}`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ dialogId }, { dispatch, queryFulfilled }) {
                    await queryFulfilled

                    dispatch(actions.removeAutoHoldingTimerFromDialog(dialogId))
                }
            }),
            addChannelToClient: build.mutation<
                IGetUserChannelsResponse,
                { omniUserId: string; projectId: string; body: AddChannelToClientRequest }
            >({
                query: ({ projectId, omniUserId, body }) => ({
                    url: `${projectId}/add_user_channel?omniUserId=${omniUserId}`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ projectId, omniUserId }, { dispatch, queryFulfilled }) {
                    const addedChannel = (await queryFulfilled).data
                    const channels = store.getState().channels.channels.data
                    const correctChannel = channels?.find(channel => channel.Id === addedChannel.channel_id)

                    if (!correctChannel) {
                        return
                    }

                    const updatedUserChannelsPatchResult = dispatch(
                        dialogsApi.util.updateQueryData(
                            "getUserChannels",
                            { projectId: projectId, omniUserId: omniUserId },
                            oldState => {
                                const channel = {
                                    ...addedChannel,
                                    channel_id: correctChannel?.Id,
                                    channel_title: correctChannel?.Title,
                                    channel_type: correctChannel?.Type
                                }

                                oldState.push(channel)
                            }
                        )
                    )

                    const updatedOperatorChannelsPatchResult = dispatch(
                        dialogsApi.util.updateQueryData(
                            "getOperatorChannels",
                            { projectId: projectId, omniUserId: omniUserId },
                            oldState => {
                                const channel = {
                                    ChannelId: correctChannel?.Id,
                                    ChannelTitle: correctChannel?.Title,
                                    ChannelType: channelTypeStringConverter.toChannelTypeString(
                                        channelTypeStringConverter.toChannelType(correctChannel?.Type)
                                    )
                                }

                                oldState.push(channel)
                            }
                        )
                    )

                    dispatch(actions.setNewUserChannelState({ isAdded: true, channelId: correctChannel.Id }))

                    queryFulfilled.catch(updatedUserChannelsPatchResult.undo)
                    queryFulfilled.catch(updatedOperatorChannelsPatchResult.undo)
                }
            }),
            resetInternalMessagesCount: build.mutation<void, { projectId: string; dialogId: string }>({
                query: ({ projectId, dialogId }) => ({
                    url: `dialog/reset-internal-messages-count?projectId=${projectId}`,
                    method: "POST",
                    body: {
                        DialogId: dialogId
                    }
                }),
                async onQueryStarted({ dialogId }, { dispatch, queryFulfilled }) {
                    const getDialogBadgesPatchResult = dispatch(
                        updateDialogBadges(oldState => {
                            const idxIntoList = oldState.findIndex(x => x.Id === dialogId)

                            if (idxIntoList > -1) {
                                oldState[idxIntoList].UnreadInternalMessages = {}
                            }
                        })
                    )

                    await queryFulfilled

                    queryFulfilled.catch(getDialogBadgesPatchResult.undo)
                }
            }),
            sendOperatorFeedback: build.mutation<void, OperatorFeedbackRequest>({
                query: body => ({
                    url: "/feedback",
                    method: "POST",
                    body
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled

                        saveOperatorFeedbackStatus()
                        dispatch(actions.setOperatorFeedbackFormButtonState(false))
                    } catch (e) {
                        handleQueryApiError(dispatch, SEND_OPERATOR_FEEDBACK_FAILED, e)
                    }
                }
            })
        }
    }
})

export type DialogsApiEndpointNames = keyof typeof dialogsApi.endpoints
export const DialogsApiEndpoints = Object.keys(dialogsApi.endpoints).reduce((obj, key) => {
    return {
        ...obj,
        [key]: key
    }
}, {} as { [K in DialogsApiEndpointNames]: K })

export const {
    useGetOperatorChannelsQuery,
    useGetCustomSectionsQuery,
    useGetCustomMenusQuery,
    useGetPinnedMessagesQuery,
    useSendMessageMutation,
    useSendInternalMessageMutation,
    useSendCommandMutation,
    useSendIntentMutation,
    useGetDialogBadgesQuery,
    useGetActiveDialogsCountQuery,
    useGetOperatorClientsBadgesQuery,
    useLazyGetOperatorClientsBadgesQuery,
    useLazyGetUserChannelsQuery,
    useLazyGetOperatorsListQuery,
    useLazyGetOperatorChannelsQuery,
    useGetOperatorClientsSectionTitleQuery,
    useLazyGetOperatorClientQuery,
    useGetDialogMessagesQuery,
    useLazyGetDialogMessagesQuery,
    useGetDialogsSurveysQuery,
    useLazyGetDialogQuery,
    usePostUpdateSlotContextMutation,
    usePostUpdateSlotContextChatUsersMutation,
    useSendFinishMutation,
    useSendOperatorOpenedDialogViewMutation,
    useSendOperatorClosedDialogViewMutation,
    useSendOperatorIsTypingMutation,
    useSendOperatorStoppedTypingMutation,
    useSetDialogChannelMutation,
    useSearchClientsByCriterionQuery,
    useLazySearchClientsByCriterionQuery,
    useSearchOmniuserByChannelIdQuery,
    useLazySearchOmniuserByChannelIdQuery,
    usePutUpdateDialogTopicsMutation,
    useGetDialogTopicsQuery,
    useSearchOperatorByCriterionQuery,
    useLazySearchOperatorByCriterionQuery,
    useOperatorDialogTransferToQueueMutation,
    useOperatorDialogTransferToOperatorMutation,
    useGetDialogTransferAvailableQueuesQuery,
    useLazyGetDialogTransferAvailableQueuesQuery,
    useGetDialogTransferAvailableQueuesByOperatorQuery,
    useLazyGetDialogTransferAvailableQueuesByOperatorQuery,
    useLazyGetDialogTopicsQuery,
    usePostEnableDialogHoldMutation,
    usePutDisableDialogHoldMutation,
    usePostDeleteMessageMutation,
    usePostEditMessageMutation,
    useStartOutgoingDialogMutation,
    useGetOperatorEmailChannelsByDialogMutation,
    useGetEmailForwardTemplateQuery,
    useGetDialogByIdQuery,
    useGetMessagesByIdQuery,
    useLazyGetDialogOldQuery,
    useLazyGetDialogHistoryBadgesQuery,
    useLazyGetLinkedToOperatorClientsQuery,
    useLazySearchLinkedClientsQuery,
    useLazyPostClientSubscriptionsQuery,
    useLazyLinkClientToOperatorQuery,
    useLazyUnlinkClientToOperatorQuery,
    useLazyLinkClientToRoleQuery,
    useLazyUnlinkClientToRoleQuery,
    useGetDialogAttachmentsQuery,
    useDeleteRoutingMessageMutation,
    useLazyGetAutoHoldingTimerQuery,
    useStopAutoHoldingTimerMutation,
    useAddChannelToClientMutation,
    useLazyGetDialogLastRerouteQuery,
    useResetInternalMessagesCountMutation,
    useGetDialogFilesPageQuery,
    useLazyGetDialogFilesPageQuery,
    useUpdateFileTitleMutation,
    useUpdateFileSectionsMutation,
    useSendOperatorFeedbackMutation
} = dialogsApi
